export const AUDIT_PIPELINE_STAGES = [
  { key: 'Application Review', shortLabel: 'App. Review', color: '#2563eb', tint: '#eff6ff' },
  { key: 'Scheduling', shortLabel: 'Scheduling', color: '#0f8f83', tint: '#ecfdf5' },
  { key: 'Audit', shortLabel: 'Audit', color: '#ea580c', tint: '#fff7ed' },
  { key: 'Technical Review', shortLabel: 'Tech. Review', color: '#7c3aed', tint: '#f5f3ff' },
  { key: 'Certificate', shortLabel: 'Certificate', color: '#d97706', tint: '#fffbeb' },
  { key: 'Completed', shortLabel: 'Completed', color: '#16a34a', tint: '#f0fdf4' },
];

const TASK_CODE_STAGE = new Map([
  ['APPLICATION_REVIEW', 'Application Review'],
  ['ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER', 'Application Review'],
  ['AUDIT_SCHEDULING', 'Scheduling'],
  ['ASSIGN_NEXT_PROCESS_SALES', 'Scheduling'],
  ['ASSIGN_NEXT_PROCESS_SCHEDULER', 'Scheduling'],
  ['AUDIT', 'Audit'],
  ['AUDIT_PLAN', 'Audit'],
  ['AUDIT_REPORT', 'Audit'],
  ['CORRECTIVE_ACTION', 'Audit'],
  ['TECHNICAL_REVIEW', 'Technical Review'],
  ['ASSIGN_NEXT_PROCESS_TECHNICAL', 'Technical Review'],
  ['ASSIGN_NEXT_PROCESS_TECHNICAL_REVIEWER', 'Technical Review'],
  ['CERTIFICATE_DECISION', 'Certificate'],
  ['CERTIFICATE_ISSUANCE', 'Certificate'],
  ['ISSUE_CERTIFICATE', 'Certificate'],
  ['ASSIGN_NEXT_PROCESS_CERTIFICATION_AUTHORITY', 'Certificate'],
  ['ASSIGN_NEXT_PROCESS_CERTIFICATE_ISSUER', 'Certificate'],
]);

const COMPLETED_STATUSES = new Set(['DONE', 'COMPLETED', 'CLOSED']);
const STAGE_RANK = new Map(AUDIT_PIPELINE_STAGES.map((stage, index) => [stage.key, index]));

const TASK_AWARENESS_BY_CODE = new Map([
  ['APPLICATION_REVIEW', { title: 'Application Review', description: 'Application Review task assigned for this client.' }],
  ['ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER', { title: 'Application Review', description: 'Application Review task assigned for this client.' }],
  ['AUDIT_SCHEDULING', { title: 'Audit Scheduling', description: 'Audit Scheduling task assigned for this client.' }],
  ['ASSIGN_NEXT_PROCESS_SALES', { title: 'Audit Scheduling', description: 'Audit Scheduling task assigned for this client.' }],
  ['ASSIGN_NEXT_PROCESS_SCHEDULER', { title: 'Audit Scheduling', description: 'Audit Scheduling task assigned for this client.' }],
]);

export const isCompletedAuditTask = (status) => COMPLETED_STATUSES.has(String(status || '').trim().toUpperCase());

export const filterOpenAuditTasks = (tasks = []) => (Array.isArray(tasks) ? tasks : [])
  .filter((task) => !isCompletedAuditTask(task?.status));

export const filterMyOpenAuditTasks = (tasks = [], user = {}) => {
  const userId = user?.id ?? user?.userId;
  const identities = new Set([user?.username, user?.email, user?.fullName, user?.name]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean));
  return filterOpenAuditTasks(tasks).filter((task) => {
    const assignedUserId = task?.assignedUserId ?? task?.assignedTo?.id;
    if (userId != null && assignedUserId != null) return String(assignedUserId) === String(userId);
    const assignedIdentity = String(task?.assignedUsername || task?.assignedEmail || '').trim().toLowerCase();
    return Boolean(assignedIdentity) && identities.has(assignedIdentity);
  });
};

export const resolveAuditTaskAwareness = (task = {}) => {
  const normalizedCode = String(task?.taskTypeCode || '').trim().toUpperCase();
  const canonical = TASK_AWARENESS_BY_CODE.get(normalizedCode);
  const title = canonical?.title || String(task?.title || task?.taskTitle || task?.taskType || task?.taskTypeCode || '').trim();
  const description = canonical?.description || String(task?.description || task?.wording || '').trim();
  if (!title && !description) return null;

  const awarenessText = `${title} ${description}`.toLowerCase();
  return {
    title,
    description,
    isUrgent: /revision required|revision requested|rejected|action required|needs attention/.test(awarenessText),
  };
};

export const resolveAuditTaskStage = (taskTypeCode) => TASK_CODE_STAGE.get(String(taskTypeCode || '').trim().toUpperCase()) || null;

export const resolveAuditProjectStage = (tasks = []) => {
  const openTasks = filterOpenAuditTasks(tasks);
  if (openTasks.length === 0) return 'Completed';

  const recognizedStages = openTasks
    .map((task) => resolveAuditTaskStage(task?.taskTypeCode))
    .filter(Boolean);

  if (recognizedStages.length === 0) return 'Audit';

  // Application Review and Audit Scheduling are created together, in parallel, at
  // project-conversion time (see AuditTaskService.createInitialAuditTasksForProject on the
  // backend) — neither gates the other's creation. Audit/Technical Review/Certificate tasks,
  // by contrast, are only ever generated once BOTH of those are done (dual-gate progression),
  // so once one of those later stages is open, any still-open AR/Scheduling row is a stale
  // leftover and must not pull the project backwards on the dashboard.
  const schedulingRank = STAGE_RANK.get('Scheduling') || 0;
  const hasAdvancedStage = recognizedStages.some((stage) => (STAGE_RANK.get(stage) || 0) > schedulingRank);

  if (hasAdvancedStage) {
    return recognizedStages.reduce((current, stage) => (
      (STAGE_RANK.get(stage) || 0) > (STAGE_RANK.get(current) || 0) ? stage : current
    ));
  }

  // Still within the initial AR/Scheduling pair: since both start open simultaneously,
  // the earliest still-open one is the real bottleneck — picking the furthest here would
  // make every fresh project read as "Scheduling" and Application Review would never show
  // a count.
  return recognizedStages.reduce((current, stage) => (
    (STAGE_RANK.get(stage) || 0) < (STAGE_RANK.get(current) || 0) ? stage : current
  ));
};

export const buildAuditPipelineStats = (tasks = []) => {
  const tasksByProject = new Map();

  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const projectId = task?.projectId;
    if (projectId == null || projectId === '') return;
    const key = String(projectId);
    tasksByProject.set(key, [...(tasksByProject.get(key) || []), task]);
  });

  const counts = Object.fromEntries(AUDIT_PIPELINE_STAGES.map((stage) => [stage.key, 0]));
  const stageMap = {};

  tasksByProject.forEach((projectTasks, projectId) => {
    const stage = resolveAuditProjectStage(projectTasks);
    stageMap[projectId] = stage;
    counts[stage] += 1;
  });

  const total = tasksByProject.size;
  return {
    stageMap,
    counts,
    total,
    active: Math.max(0, total - counts.Completed),
  };
};

export const buildAuditPipelineGradient = (counts = {}, total = 0) => {
  if (!total) return '#e8eef2';
  let cursor = 0;
  const stops = [];

  AUDIT_PIPELINE_STAGES.forEach((stage) => {
    const share = Math.max(0, Number(counts?.[stage.key]) || 0) / total * 100;
    const start = cursor;
    cursor += share;
    if (share > 0) stops.push(`${stage.color} ${start}% ${cursor}%`);
  });

  return stops.length > 0 ? `conic-gradient(${stops.join(', ')})` : '#e8eef2';
};
